import { useState } from 'react';
import { useLocation } from 'react-router';
import {
  ArrowDownToLine,
  CircleAlert,
  CircleCheck,
  LoaderCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/cn';
import { useAppUpdates, useUpdateRequired, type UpdateState } from '@/lib/updates';

export function UpdateBanner() {
  const u = useAppUpdates();
  const mandatory = useUpdateRequired();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!u || mandatory || location.pathname.startsWith('/watch')) return null;
  const { state } = u;
  const visible =
    ['available', 'downloading', 'ready'].includes(state.status) ||
    (state.status === 'error' && !!state.version);
  if (!visible || dismissed === `${state.version}:${state.status}`) return null;

  const dismiss = () => setDismissed(`${state.version}:${state.status}`);
  const downloading = state.status === 'downloading';
  const view =
    state.status === 'available'
      ? {
          icon: <ArrowDownToLine />,
          title: 'Update available',
          line: `Version ${state.version}`,
          action: { label: 'Update now', run: () => void u.download() },
        }
      : downloading
        ? {
            icon: <LoaderCircle className="animate-spin" />,
            title: 'Downloading update',
            line: `${state.progress ?? 0}% · Keep watching meanwhile`,
            action: null,
          }
        : state.status === 'ready'
          ? {
              icon: <RefreshCw />,
              title: 'Update ready',
              line: 'Reopens where you left off',
              action: { label: 'Restart Tea+', run: () => void u.install() },
            }
          : {
              icon: <CircleAlert />,
              title: "The update didn't finish",
              line: state.error ?? 'Nothing changed on your PC.',
              action: { label: 'Try again', run: () => void u.check() },
            };

  return (
    <div
      role="status"
      aria-label="App update"
      className="fixed bottom-24 right-4 z-[60] w-[min(23rem,calc(100vw-2rem))] animate-[rise_380ms_var(--ease-out-soft)] lg:bottom-6 lg:right-6"
    >
      <div className="overflow-hidden rounded-2xl border border-line bg-bg-2/95 shadow-[0_24px_60px_-20px_rgba(0,0,0,.85)] backdrop-blur-xl">
        <div className="flex items-center gap-3 p-3.5">
          <span
            className={cn(
              'grid h-10 w-10 shrink-0 place-items-center rounded-full [&_svg]:h-[18px] [&_svg]:w-[18px]',
              state.status === 'error' ? 'bg-warning/15 text-warning' : 'bg-pink/15 text-pink',
            )}
          >
            {view.icon}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{view.title}</p>
            <p className="truncate text-xs text-muted">{view.line}</p>
          </div>
          {view.action && (
            <Button size="sm" onClick={view.action.run} className="shrink-0">
              {view.action.label}
            </Button>
          )}
          {!downloading && (
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="-mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted transition hover:bg-white/8 hover:text-text"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {downloading && (
          <div className="h-1 bg-white/8">
            <div
              className="h-full bg-pink transition-[width] duration-300"
              style={{ width: `${state.progress ?? 0}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function UpdateActions({
  state,
  download,
  install,
  check,
  className,
}: {
  state: UpdateState;
  download: () => Promise<unknown>;
  install: () => Promise<unknown>;
  check: () => Promise<unknown>;
  className?: string;
}) {
  return (
    <div className={className}>
      {state.status === 'available' && (
        <Button className="w-full" onClick={() => void download()}>
          <ArrowDownToLine className="h-4 w-4" /> Update
        </Button>
      )}
      {(state.status === 'downloading' || state.status === 'reinstalling') && (
        <div aria-live="polite">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-muted">
              <LoaderCircle className="h-4 w-4 animate-spin text-pink" />{' '}
              {state.status === 'reinstalling'
                ? 'Downloading Tea+… it will close and reopen when done.'
                : 'Downloading update…'}
            </span>
            <span className="tabular-nums">{state.progress ?? 0}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-pink-hover via-pink to-pink-soft transition-[width] duration-300"
              style={{ width: `${state.progress ?? 0}%` }}
            />
          </div>
        </div>
      )}
      {state.status === 'ready' && (
        <Button className="w-full" onClick={() => void install()}>
          <RefreshCw className="h-4 w-4" /> Restart to update
        </Button>
      )}
      {state.status === 'error' && (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-warning">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" /> {state.error}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void check()}>
            Try again
          </Button>
        </div>
      )}
      {state.status === 'reinstall-ready' && (
        <p className="flex items-center gap-2 text-sm text-success">
          <CircleCheck className="h-4 w-4" /> Installer downloaded and verified.
        </p>
      )}
    </div>
  );
}
